/* eslint-disable */
/*
 * matroska-surgeon.js — rebuild the header of a Matroska/WebM whose own header is gone.
 *
 * WHY THIS EXISTS
 * ---------------
 * MP4 has a payload anchor. When a recording loses its `moov`, `media-locate`
 * still finds the `mdat`, and `mp4-untrunc.js` reconstructs a sample table
 * behind it. Matroska had neither: the locator's only Matroska anchor was the
 * EBML magic at the very front of the file, so a WebM whose first bytes were
 * zeroed matched nothing anywhere and came back `kind: 'none'` — "we searched
 * every byte and there is no media in this file". That verdict is FALSE for a
 * file whose Clusters are all present, and it is the worst kind of false: it
 * disables the repair button, so the user is told to stop.
 *
 * Support #1622 (2026-09-09, RU, Yandex 26, 519 MB .webm): FFmpeg answered
 * "0x00 at pos 0 (0x0) invalid as first byte of an EBML number" to all thirteen
 * repair strategies over ~15 minutes, and the tool closed with "Не удалось
 * восстановить файл".
 *
 * WHAT MAKES IT EASIER THAN UNTRUNC
 * ---------------------------------
 * Matroska timing is per-block, not in a global index: every SimpleBlock carries
 * its own track number and timestamp, and every Cluster carries its own absolute
 * timestamp. So nothing has to be RECONSTRUCTED — only DECLARED. A few hundred
 * bytes of EBML header + Info + Tracks in front of the surviving Clusters, and
 * the file plays. Measured on the damage matrix in
 * tests/playwright/matroska-surgeon-matrix.mjs: 300/300 video frames and
 * 501/501 audio packets recovered from a file the shipped tool called empty.
 *
 * The one thing that must be learned rather than declared is WHICH CODEC each
 * track holds, because CodecID lives in the Tracks element that was destroyed.
 * Two lanes answer that, in this order:
 *
 *   sniff      For every codec whose bitstream describes itself — VP8, VP9, AV1,
 *              Opus, Vorbis, MP3 — the frames in the surviving blocks say what
 *              they are, including the picture size. That covers WebM whole,
 *              which is where browser recorders, OBS and YouTube downloads live.
 *   reference  For H.264/HEVC/AAC in MKV the decoder configuration lives ONLY in
 *              CodecPrivate and cannot be inferred. There a donor clip from the
 *              same source supplies the header verbatim — the same bargain
 *              untrunc strikes, but far cheaper here, because a Matroska header
 *              is a self-contained prefix rather than an index over the payload.
 *
 * NOTHING HERE DECIDES WHETHER THE RESULT IS GOOD. `rebuild()` returns a
 * candidate; the caller verifies it by decoding, exactly as repair-video already
 * does for every other lane. A header that describes the frames wrongly produces
 * a file that fails that check, which is the honest way to find out.
 *
 * Public API
 *   MatroskaSurgeon.findClusters(file, opts)  → {at, count, ok} | null
 *   MatroskaSurgeon.examine(file, {at})       → {tracks, docType, timestampScale}
 *   MatroskaSurgeon.rebuild(file, opts)       → {blob, tracks, header} | null
 *   MatroskaSurgeon.headerFrom(referenceFile) → Uint8Array | null  (donor lane)
 *   MatroskaSurgeon.CLUSTER_ID                → the 4 anchor bytes, shared with media-locate
 */
/** Cluster element ID. The payload anchor — Matroska's `mdat`. */
var CLUSTER_ID = [0x1f, 0x43, 0xb6, 0x75];

var ID_EBML = '1a45dfa3', ID_SEGMENT = '18538067', ID_INFO = '1549a966', ID_TRACKS = '1654ae6b';

/* ── EBML primitives ───────────────────────────────────────────────────── */

function hexBytes(hex) {
    var out = new Uint8Array(hex.length / 2);
    for (var i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
    return out;
}

/** Encode a data size as an EBML variable-length integer, shortest form. */
function vint(n) {
    for (var len = 1; len <= 8; len++) {
        var max = Math.pow(2, 7 * len) - 1;   // all-ones is reserved for "unknown"
        if (n < max) {
            var b = new Uint8Array(len), v = n;
            for (var i = len - 1; i >= 0; i--) { b[i] = v % 256; v = Math.floor(v / 256); }
            b[0] |= 0x80 >> (len - 1);
            return b;
        }
    }
    throw new Error('vint too large');
}

/**
 * Read a vint at `pos`. `isId` keeps the marker bits (element IDs are written
 * with them); a data size strips them.
 */
function readVint(buf, pos, isId) {
    var first = buf[pos];
    if (first === undefined) return null;
    var len = 1;
    for (var m = 0x80; m && !(first & m); m >>= 1) len++;
    if (len > 8 || pos + len > buf.length) return null;
    var v = isId ? first : (first & (0xff >> len));
    var allOnes = !isId && (first & (0xff >> len)) === (0xff >> len);
    for (var i = 1; i < len; i++) {
        v = v * 256 + buf[pos + i];
        if (!isId && buf[pos + i] !== 0xff) allOnes = false;
    }
    return { value: v, len: len, unknown: allOnes };
}

function concat(chunks) {
    var n = 0, i;
    for (i = 0; i < chunks.length; i++) n += chunks[i].length;
    var out = new Uint8Array(n), o = 0;
    for (i = 0; i < chunks.length; i++) { out.set(chunks[i], o); o += chunks[i].length; }
    return out;
}

function el(idHex, payload) { return concat([hexBytes(idHex), vint(payload.length), payload]); }
function uintBytes(n) { var b = []; do { b.unshift(n % 256); n = Math.floor(n / 256); } while (n); return new Uint8Array(b); }
function f64Bytes(n) { var b = new Uint8Array(8); new DataView(b.buffer).setFloat64(0, n); return b; }
function strBytes(s) {
    var b = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 0xff;
    return b;
}

/* ── bit reader, for the codec headers that are bit-packed ─────────────── */
function bits(buf, at) {
    var p = at * 8;
    return {
        u: function (n) {
            var v = 0;
            for (var i = 0; i < n; i++) {
                var byteAt = p >> 3;
                if (byteAt >= buf.length) return v << (n - i - 1);
                v = (v * 2) + ((buf[byteAt] >> (7 - (p & 7))) & 1);
                p++;
            }
            return v;
        },
        skip: function (n) { p += n; },
    };
}

/* ── codec identification from the frames themselves ───────────────────── */

/** VP8 keyframe: 3-byte frame tag, sync 9D 01 2A, then 14-bit width/height. */
function vp8Keyframe(f) {
    if (f.length < 10) return null;
    if ((f[0] & 1) !== 0) return null;                       // frame type 0 = key
    if (f[3] !== 0x9d || f[4] !== 0x01 || f[5] !== 0x2a) return null;
    var w = ((f[7] << 8) | f[6]) & 0x3fff;
    var h = ((f[9] << 8) | f[8]) & 0x3fff;
    if (w < 16 || h < 16 || w > 16384 || h > 16384) return null;
    return { codec: 'V_VP8', width: w, height: h };
}

/**
 * VP9 keyframe uncompressed header. The sync code is what makes this safe to
 * claim; the picture size sits a known number of bits behind it, and the bit
 * count depends on the profile and colour config, so those are walked rather
 * than assumed.
 */
function vp9Keyframe(f) {
    if (f.length < 12) return null;
    var br = bits(f, 0);
    if (br.u(2) !== 2) return null;                          // frame_marker
    var lo = br.u(1), hi = br.u(1), profile = (hi << 1) + lo;
    if (profile === 3) br.skip(1);
    if (br.u(1) !== 0) return null;                          // show_existing_frame
    if (br.u(1) !== 0) return null;                          // frame_type: 0 = KEY
    br.skip(2);                                              // show_frame, error_resilient
    if (br.u(8) !== 0x49 || br.u(8) !== 0x83 || br.u(8) !== 0x42) return null;
    if (profile >= 2) br.skip(1);                            // ten_or_twelve_bit
    var cs = br.u(3);
    if (cs !== 7) {                                          // 7 = SRGB
        br.skip(1);                                          // color_range
        if (profile === 1 || profile === 3) br.skip(3);      // subsampling x/y + reserved
    } else if (profile === 1 || profile === 3) br.skip(1);
    var w = br.u(16) + 1, h = br.u(16) + 1;
    if (w < 16 || h < 16 || w > 16384 || h > 16384) return null;
    return { codec: 'V_VP9', width: w, height: h };
}

/** leb128, as AV1 writes OBU sizes. */
function leb128(f, at) {
    var v = 0, n = 0;
    for (; n < 8 && at + n < f.length; n++) {
        v |= (f[at + n] & 0x7f) << (n * 7);
        if (!(f[at + n] & 0x80)) return { value: v >>> 0, len: n + 1 };
    }
    return null;
}

/**
 * AV1 sequence header OBU, when the stream carries one in band (encoders emit
 * one ahead of each keyframe temporal unit).
 *
 * ⚠ This one has no magic number — an OBU header is a single byte with four
 * meaningful bits — so it is the sniffer that can be passed by accident, and
 * it was: scanning the first eight bytes for an OBU-shaped byte claimed the
 * OPUS and VORBIS tracks of the matrix as AV1 video (measured, 2026-09-09,
 * 6 of 7 WebM cases mis-typed). Three things make the claim safe instead:
 * the sequence header must be the FIRST OBU of the block (AV1 in Matroska is
 * one temporal unit per block, optionally opening with a temporal delimiter),
 * its declared size must fit the frame, and the OBU after it must parse too —
 * a chain junk does not produce.
 */
function av1Sequence(f) {
    var starts = [0];
    if (f.length > 4 && f[0] === 0x12 && f[1] === 0x00) starts.push(2);  // temporal delimiter
    for (var s = 0; s < starts.length; s++) {
        var at = starts[s];
        var h = f[at];
        if (h === undefined || (h & 0x80)) continue;          // forbidden bit
        if (((h >> 3) & 0xf) !== 1) continue;                 // OBU_SEQUENCE_HEADER
        var ext = (h >> 2) & 1, hasSize = (h >> 1) & 1;
        if (!hasSize || (h & 1)) continue;                    // reserved bit must be 0
        var p = at + 1 + ext;
        var sz = leb128(f, p);
        if (!sz || sz.value < 4 || p + sz.len + sz.value > f.length) continue;
        var body = p + sz.len;
        var br = bits(f, body);
        if (br.u(3) > 2) continue;                            // seq_profile 0..2
        br.skip(1);                                           // still_picture
        var reduced = br.u(1);
        if (reduced) { br.skip(5); }
        else {
            if (br.u(1)) continue;                            // timing_info_present — not walked
            if (br.u(1)) continue;                            // initial_display_delay_present
            var n = br.u(5);                                  // operating_points_cnt_minus_1
            for (var i = 0; i <= n; i++) {
                br.skip(12);                                  // operating_point_idc
                if (br.u(5) > 7) br.skip(1);                  // seq_level_idx, then seq_tier
            }
        }
        var wb = br.u(4) + 1, hb = br.u(4) + 1;
        var w = br.u(wb) + 1, hgt = br.u(hb) + 1;
        if (w < 16 || hgt < 16 || w > 16384 || hgt > 16384) continue;
        // The OBU after the sequence header must itself be a legal header —
        // one more byte of corroboration, and the one that separates a real
        // stream from a byte that happened to look like an OBU.
        var next = body + sz.value;
        if (next < f.length) {
            var nh = f[next];
            if (nh & 0x80) continue;
            var nt = (nh >> 3) & 0xf;
            if (nt !== 2 && nt !== 3 && nt !== 4 && nt !== 6 && nt !== 7 && nt !== 15) continue;
        }
        return { codec: 'V_AV1', width: w, height: hgt };
    }
    return null;
}

/** Vorbis packets in Matroska start with a packet-type byte and "vorbis". */
function vorbisFrame(f) {
    return f.length > 7 && f[1] === 0x76 && f[2] === 0x6f && f[3] === 0x72 &&
           f[4] === 0x62 && f[5] === 0x69 && f[6] === 0x73;
}

/** MPEG-1/2 audio frame sync + a sane bitrate/rate pair. */
function mpegAudioFrame(f) {
    if (f.length < 4 || f[0] !== 0xff || (f[1] & 0xe0) !== 0xe0) return false;
    if (((f[1] >> 3) & 3) === 1) return false;               // reserved version
    if (((f[1] >> 1) & 3) === 0) return false;               // reserved layer
    return ((f[2] >> 4) & 0xf) !== 0xf && ((f[2] >> 2) & 3) !== 3;
}

/**
 * Opus.
 *
 * ⚠ The first version of this asked only that the TOC's configuration number be
 * the SAME in every sampled packet — a cadence, not a proof — and H.264 walked
 * straight through it: video in Matroska is AVCC, every frame opens with a
 * 4-byte length whose high byte is 0x00 for anything under 16 MB, so every
 * packet reported "config 0" and a 640x360 H.264 track was declared Opus AUDIO.
 * The rebuilt file then passed the tool's verifier and decoded to nothing
 * (measured 2026-09-09 on corpus/header-zeroed.mkv). A wrong answer offered as
 * a repair is worse than no answer, so the claim now needs an arithmetic one:
 *
 *   the TOC states the frame duration and the frame count, the block states the
 *   byte count, and the bitrate that follows must be one Opus can actually
 *   produce (6–510 kbit/s per channel; ±headroom taken generously).
 *
 * H.264's 4891-byte "10 ms SILK narrowband frame" implies 3913 kbit/s and is
 * rejected on the first packet.
 */
function opusFrameMs(cfg) {
    if (cfg < 12) return [10, 20, 40, 60][cfg & 3];       // SILK NB/MB/WB
    if (cfg < 16) return [10, 20][cfg & 1];               // Hybrid SWB/FB
    return [2.5, 5, 10, 20][cfg & 3];                     // CELT
}

/** Opus needs at least this many sampled packets before the cadence is one. */
var OPUS_MIN_PACKETS = 3;

function opusFrames(frames, lens) {
    if (!frames.length || !lens || lens.length !== frames.length) return false;
    /* A cadence of one packet is not a cadence. One AAC frame (TOC-shaped byte
     * 0xDC, 312 bytes) computes to 125 kbit/s at CELT's 20 ms and walks straight
     * through the arithmetic; three consecutive packets agreeing on the
     * configuration number is what AAC does not do, because its first byte
     * carries no such field. Found by tests/node/matroska-surgeon.test.mjs on
     * the day it was written — the single-frame fixture was the realistic one to
     * write, and the code only survived real files because examine() happens to
     * sample eight. */
    if (frames.length < OPUS_MIN_PACKETS) return false;
    var cfg = frames[0][0] >> 3;
    for (var i = 0; i < frames.length; i++) {
        var f = frames[i];
        if (f.length < 2) return false;
        if ((f[0] >> 3) !== cfg) return false;            // one stream, one config
        var code = f[0] & 3, n = 1;
        if (code === 1 || code === 2) n = 2;
        else if (code === 3) { n = f[1] & 0x3f; if (n < 1 || n > 48) return false; }
        var ms = opusFrameMs(cfg) * n;
        if (ms <= 0 || ms > 120) return false;            // Opus packets are ≤ 120 ms
        var kbps = (lens[i] * 8) / ms;                    // bytes·8 / ms === kbit/s
        if (kbps < 4 || kbps > 1100) return false;        // 510 kbit/s per channel, ×2
    }
    return true;
}

function sniffVideo(frames) {
    for (var i = 0; i < frames.length; i++) {
        var f = frames[i];
        var r = vp9Keyframe(f) || vp8Keyframe(f) || av1Sequence(f);
        if (r) return r;
    }
    return null;
}

function sniffAudio(frames, lens, avgBytes) {
    for (var i = 0; i < frames.length; i++) {
        if (vorbisFrame(frames[i])) return { codec: 'A_VORBIS' };
        if (mpegAudioFrame(frames[i])) return { codec: 'A_MPEG/L3' };
    }
    // Opus last: its signature is arithmetic rather than a magic number, so it
    // must not pre-empt a format that carries one.
    if (avgBytes > 0 && avgBytes < 4000 && opusFrames(frames, lens)) return { codec: 'A_OPUS' };
    return null;
}

/* ── reading the surviving clusters ────────────────────────────────────── */

async function readAt(file, at, len) {
    var end = Math.min(at + len, file.size);
    if (end <= at) return new Uint8Array(0);
    return new Uint8Array(await file.slice(at, end).arrayBuffer());
}

/**
 * Is `buf[i]` a real Cluster header? Four anchor bytes over a file that
 * averages 211 MB is a hypothesis, not a finding (media-locate's own rule),
 * so the size must parse AND the first child must be an element a Cluster
 * actually contains.
 */
function clusterHere(buf, i) {
    if (buf[i] !== CLUSTER_ID[0] || buf[i + 1] !== CLUSTER_ID[1] ||
        buf[i + 2] !== CLUSTER_ID[2] || buf[i + 3] !== CLUSTER_ID[3]) return false;
    var sz = readVint(buf, i + 4, false);
    if (!sz) return false;
    var at = i + 4 + sz.len;
    /* Walk the first few children. CRC-32 (BF) and Void (EC) are legal
     * anywhere in EBML and mkvmuxer writes a CRC-32 as the FIRST child of
     * every Cluster — demanding a Cluster-specific ID immediately found none
     * of the four MKV cases in the matrix while finding all seven WebM ones,
     * which is the shape of a validator that has only ever met one muxer. */
    for (var hop = 0; hop < 4; hop++) {
        var cid = readVint(buf, at, true);
        if (!cid) return false;
        var csz = readVint(buf, at + cid.len, false);
        if (!csz) return false;
        var b0 = buf[at];
        // Timestamp (E7), Position (A7), PrevSize (AB), SimpleBlock (A3),
        // BlockGroup (A0), EncryptedBlock (AF), SilentTracks (5854).
        if (cid.len === 1 && (b0 === 0xe7 || b0 === 0xa7 || b0 === 0xab ||
                              b0 === 0xa3 || b0 === 0xa0 || b0 === 0xaf)) return true;
        if (cid.len === 2 && b0 === 0x58 && buf[at + 1] === 0x54) return true;
        if (cid.len === 1 && (b0 === 0xbf || b0 === 0xec)) {   // CRC-32 / Void — skip and look on
            if (csz.unknown || csz.value > 64) return false;
            at = at + cid.len + csz.len + csz.value;
            continue;
        }
        return false;
    }
    return false;
}

/**
 * Bytes the Cluster at `i` occupies, or 0 when its size is unknown or unreadable
 * (a Cluster may legally declare an unknown size, and the last one often does).
 * Lives here rather than in media-locate because it is EBML vint arithmetic, and
 * a second copy of that is how the same bytes get two stories.
 */
function clusterSpan(buf, i) {
    var sz = readVint(buf, i + 4, false);
    if (!sz || sz.unknown) return 0;
    return 4 + sz.len + sz.value;
}

/**
 * Find where the Clusters start. `from` lets the caller skip a prefix it has
 * already ruled out. Reads in slices and keeps nothing between them, so the
 * cost is one sequential pass and the memory is one slice.
 */
async function findClusters(file, opts) {
    var o = opts || {};
    var SLICE = 4 * 1048576, OVERLAP = 32;
    var from = o.from || 0, limit = o.limit === undefined ? file.size : Math.min(file.size, o.limit);
    for (var base = from; base < limit; base += SLICE - OVERLAP) {
        if (o.signal && o.signal.aborted) return null;
        var buf = await readAt(file, base, SLICE);
        if (buf.length < 16) break;
        for (var i = 0; i + 16 <= buf.length; i++) {
            if (buf[i] !== CLUSTER_ID[0]) continue;
            if (!clusterHere(buf, i)) continue;
            var at = base + i;
            // A single Cluster header is cheap to fake; a CHAIN is not. Walk
            // forward and require the next header to land exactly where the
            // first one's size says it will, or that the first runs to EOF.
            var count = await countClusters(file, at, 4);
            if (count >= 2 || (count === 1 && at + 4096 < file.size)) {
                return { at: at, count: count, ok: true };
            }
        }
        if (o.onProgress) o.onProgress(Math.min(1, (base + buf.length) / limit));
    }
    return null;
}

/** How many Clusters chain from `at`? Header-only walk, `max` hops. */
async function countClusters(file, at, max) {
    var pos = at, n = 0;
    for (var hop = 0; hop < (max || 64); hop++) {
        var head = await readAt(file, pos, 16);
        if (head.length < 8) break;
        if (!clusterHere(head, 0)) break;
        n++;
        var sz = readVint(head, 4, false);
        if (!sz || sz.unknown) { n = Math.max(n, 1); break; }   // unknown size: last one
        pos = pos + 4 + sz.len + sz.value;
        if (pos >= file.size) break;
    }
    return n;
}

/**
 * Learn the track layout from the blocks. Reads at most `maxBytes` from `at`
 * — a few clusters is plenty, and a 519 MB file must not be pulled into the
 * heap to answer a question the first megabyte settles.
 */
async function examine(file, opts) {
    var o = opts || {};
    var at = o.at, maxBytes = o.maxBytes || (12 * 1048576);
    var buf = await readAt(file, at, maxBytes);
    var tracks = {}, clusters = 0, lastTs = 0, pos = 0;

    while (pos + 4 <= buf.length && clusters < 512) {
        if (!clusterHere(buf, pos)) break;
        var sz = readVint(buf, pos + 4, false);
        if (!sz) break;
        var cEnd = sz.unknown ? buf.length : Math.min(buf.length, pos + 4 + sz.len + sz.value);
        var p = pos + 4 + sz.len;
        clusters++;
        while (p < cEnd) {
            var eid = readVint(buf, p, true); if (!eid) break;
            var esz = readVint(buf, p + eid.len, false); if (!esz) break;
            var dataAt = p + eid.len + esz.len, dataEnd = dataAt + esz.value;
            if (esz.unknown || dataEnd > buf.length) break;
            if (eid.len === 1 && buf[p] === 0xe7) {           // Timestamp
                var t = 0;
                for (var k = dataAt; k < dataEnd; k++) t = t * 256 + buf[k];
                if (t > lastTs) lastTs = t;
            }
            var blockAt = -1, blockEnd = -1;
            if (eid.len === 1 && buf[p] === 0xa3) { blockAt = dataAt; blockEnd = dataEnd; }
            else if (eid.len === 1 && buf[p] === 0xa0) {      // BlockGroup → find the Block
                var q = dataAt;
                while (q < dataEnd) {
                    var bid = readVint(buf, q, true); if (!bid) break;
                    var bsz = readVint(buf, q + bid.len, false); if (!bsz) break;
                    var bAt = q + bid.len + bsz.len;
                    if (bid.len === 1 && buf[q] === 0xa1) { blockAt = bAt; blockEnd = bAt + bsz.value; break; }
                    q = bAt + bsz.value;
                }
            }
            if (blockAt >= 0) {
                var tn = readVint(buf, blockAt, false);
                if (tn) {
                    var flags = buf[blockAt + tn.len + 2];
                    var frameAt = blockAt + tn.len + 3;
                    var laced = (flags & 0x06) !== 0;
                    var rec = tracks[tn.value] || (tracks[tn.value] = { number: tn.value, n: 0, bytes: 0, frames: [], lens: [] });
                    rec.n++;
                    rec.bytes += Math.max(0, blockEnd - frameAt);
                    // Laced blocks pack several frames behind a count; the
                    // codec sniffers want ONE frame, so those are skipped
                    // rather than mis-parsed. Unlaced blocks are the norm for
                    // video and for Opus.
                    if (!laced && rec.frames.length < 8 && blockEnd > frameAt) {
                        /* The HEAD is what the codec sniffers read, but the TRUE
                         * length is what the Opus bitrate check needs — sampling
                         * 96 bytes and then dividing those by the frame duration
                         * made every packet look like a plausible 77 kbit/s, and
                         * that is how an H.264 track was declared Opus. */
                        rec.frames.push(buf.subarray(frameAt, Math.min(blockEnd, frameAt + 96)));
                        rec.lens.push(blockEnd - frameAt);
                    }
                }
            }
            p = dataEnd;
        }
        pos = cEnd;
    }

    var out = [];
    for (var key in tracks) {
        if (!Object.prototype.hasOwnProperty.call(tracks, key)) continue;
        var r = tracks[key];
        var avg = r.n ? r.bytes / r.n : 0;
        var v = sniffVideo(r.frames);
        if (v) { out.push({ number: r.number, type: 'video', codec: v.codec, width: v.width, height: v.height, blocks: r.n, avgBytes: Math.round(avg) }); continue; }
        var a = sniffAudio(r.frames, r.lens, avg);
        if (a) { out.push({ number: r.number, type: 'audio', codec: a.codec, blocks: r.n, avgBytes: Math.round(avg) }); continue; }
        out.push({ number: r.number, type: null, codec: null, blocks: r.n, avgBytes: Math.round(avg) });
    }
    out.sort(function (x, y) { return x.number - y.number; });
    return { tracks: out, clusters: clusters, lastClusterTs: lastTs };
}

/**
 * How long is what survived? Read from the LAST Cluster, not the first ones.
 *
 * Without this the rebuilt Info carries no Duration, the Segment size is
 * unknown, and FFmpeg answers `Duration: N/A` — which repair-video's verifier
 * rejects outright (`verify_reject: no_duration_parse`, 307 of those in 90 days
 * across all lanes). A player has nothing to draw a seek bar from either. Two
 * slices of the tail settle it: the last Cluster's absolute timestamp plus the
 * largest block timecode inside it.
 */
async function tailDurationMs(file, at, scaleNs) {
    var TAIL = 8 * 1048576;
    var from = Math.max(at, file.size - TAIL);
    var buf = await readAt(file, from, TAIL);
    var lastAt = -1;
    for (var i = 0; i + 16 <= buf.length; i++) {
        if (buf[i] !== CLUSTER_ID[0]) continue;
        if (buf[i + 1] !== CLUSTER_ID[1] || buf[i + 2] !== CLUSTER_ID[2] || buf[i + 3] !== CLUSTER_ID[3]) continue;
        if (clusterHere(buf, i)) lastAt = i;
    }
    if (lastAt < 0) return 0;
    var sz = readVint(buf, lastAt + 4, false);
    if (!sz) return 0;
    var p = lastAt + 4 + sz.len;
    var end = sz.unknown ? buf.length : Math.min(buf.length, lastAt + 4 + sz.len + sz.value);
    var base = 0, rel = 0;
    while (p < end) {
        var eid = readVint(buf, p, true); if (!eid) break;
        var esz = readVint(buf, p + eid.len, false); if (!esz || esz.unknown) break;
        var dataAt = p + eid.len + esz.len, dataEnd = dataAt + esz.value;
        if (dataEnd > buf.length) break;
        if (eid.len === 1 && buf[p] === 0xe7) {
            base = 0;
            for (var k = dataAt; k < dataEnd; k++) base = base * 256 + buf[k];
        } else if (eid.len === 1 && (buf[p] === 0xa3 || buf[p] === 0xa0)) {
            var bAt = dataAt;
            if (buf[p] === 0xa0) {                                // BlockGroup → its Block
                var q = dataAt, found = -1;
                while (q < dataEnd) {
                    var bid = readVint(buf, q, true); if (!bid) break;
                    var bsz = readVint(buf, q + bid.len, false); if (!bsz) break;
                    if (bid.len === 1 && buf[q] === 0xa1) { found = q + bid.len + bsz.len; break; }
                    q = q + bid.len + bsz.len + bsz.value;
                }
                if (found < 0) { p = dataEnd; continue; }
                bAt = found;
            }
            var tn = readVint(buf, bAt, false);
            if (tn) {
                var t = (buf[bAt + tn.len] << 8) | buf[bAt + tn.len + 1];
                if (t & 0x8000) t -= 0x10000;                     // int16, signed
                if (t > rel) rel = t;
            }
        }
        p = dataEnd;
    }
    // Timestamps are in TimestampScale units; Duration is written in the same
    // units, so no conversion — only the last frame's own length is unknown, and
    // one frame short of the truth is not worth guessing at.
    var total = base + rel;
    return total > 0 ? total : 0;
}

/* ── writing the header ────────────────────────────────────────────────── */

function buildHeader(spec) {
    var docType = spec.docType || 'matroska';
    var ebml = el(ID_EBML, concat([
        el('4286', uintBytes(1)),                            // EBMLVersion
        el('42f7', uintBytes(1)),                            // EBMLReadVersion
        el('42f2', uintBytes(4)),                            // EBMLMaxIDLength
        el('42f3', uintBytes(8)),                            // EBMLMaxSizeLength
        el('4282', strBytes(docType)),
        el('4287', uintBytes(docType === 'webm' ? 2 : 4)),
        el('4285', uintBytes(2)),
    ]));

    var infoParts = [el('2ad7b1', uintBytes(spec.timestampScale || 1000000))];
    if (spec.durationMs) infoParts.push(el('4489', f64Bytes(spec.durationMs)));
    infoParts.push(el('4d80', strBytes('Timbrica repair-video')));
    infoParts.push(el('5741', strBytes('Timbrica repair-video')));
    var info = el(ID_INFO, concat(infoParts));

    /* EVERY track that has blocks must be declared, identified or not. A
     * Matroska demuxer refuses a block whose track number it has never seen
     * — FFmpeg answers "Invalid track number 2" and abandons the file — so
     * dropping the one track we could not name also threw away the one we
     * could (measured: a VP8+Vorbis rebuild that named only the VP8 track
     * demuxed to nothing at all). An unnamed track is declared with a CodecID
     * no decoder claims, which leaves it inert and its neighbours playable.
     */
    var entries = [];
    for (var i = 0; i < spec.tracks.length; i++) {
        var t = spec.tracks[i];
        if (!t.codec) {
            if (!t.blocks) continue;
            /* Declared as a SUBTITLE track, and the choice was measured rather
             * than reasoned. Five candidates were built and put through the same
             * `-c copy` the repair ladder runs:
             *
             *   A_TIMBRICA/UNIDENTIFIED  probes fine, remux dies  ("No wav codec tag")
             *   A_MS/ACM                 probes fine, remux dies  (same)
             *   A_VORBIS                 probe dies              ("Extradata missing")
             *   V_UNCOMPRESSED           probe dies              ("Invalid pixel format")
             *   S_TEXT/UTF8              probe ok, remux ok       ← this one
             *
             * A subtitle track is the only shape FFmpeg carries as opaque bytes
             * without demanding to understand them, so the tracks we COULD name
             * survive the remux instead of being lost with the one we could not.
             * Marked not-default and not-enabled with a name that says what it
             * is, so no player surfaces it as content. */
            entries.push(el('ae', concat([
                el('d7', uintBytes(t.number)), el('73c5', uintBytes(t.number)),
                el('83', uintBytes(17)),                 // TrackType 17 = subtitle
                el('9c', uintBytes(0)),                  // FlagLacing
                el('b9', uintBytes(0)),                  // FlagEnabled
                el('88', uintBytes(0)),                  // FlagDefault
                el('536e', strBytes('unrecovered stream')),
                el('86', strBytes('S_TEXT/UTF8')),
            ])));
            continue;
        }
        var parts = [
            el('d7', uintBytes(t.number)),                   // TrackNumber
            el('73c5', uintBytes(t.number)),                 // TrackUID
            el('83', uintBytes(t.type === 'video' ? 1 : 2)), // TrackType
            el('9c', uintBytes(0)),                          // FlagLacing
            el('86', strBytes(t.codec)),                     // CodecID
        ];
        if (t.type === 'video') {
            parts.push(el('e0', concat([
                el('b0', uintBytes(t.width)), el('ba', uintBytes(t.height)),
            ])));
        } else {
            if (t.codec === 'A_OPUS') {
                // A minimal OpusHead. Opus always decodes at 48 kHz; the
                // channel count is the only field a wrong guess is audible in,
                // and stereo is what every browser recorder and every phone
                // writes. Pre-skip 312 is libopus's own default.
                var ch = t.channels || 2;
                parts.push(el('63a2', concat([
                    strBytes('OpusHead'),
                    new Uint8Array([1, ch]),
                    new Uint8Array([0x38, 0x01]),            // pre-skip 312, LE
                    new Uint8Array([0x80, 0xbb, 0x00, 0x00]),// input rate 48000, LE
                    new Uint8Array([0, 0]),                  // output gain
                    new Uint8Array([0]),                     // channel mapping family 0
                ])));
            }
            parts.push(el('e1', concat([
                el('b5', f64Bytes(t.sampleRate || 48000)),
                el('9f', uintBytes(t.channels || 2)),
            ])));
        }
        entries.push(el('ae', concat(parts)));
    }
    var tracks = el(ID_TRACKS, concat(entries));

    // Segment size UNKNOWN — the only honest length for a file whose tail may
    // be cut, and legal Matroska. A declared size that overruns the data is
    // what makes players stop early on a truncated recording.
    var segHdr = concat([hexBytes(ID_SEGMENT),
                         new Uint8Array([0x01, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff])]);
    return concat([ebml, segHdr, info, tracks]);
}

/**
 * A donor's header, verbatim: everything from its EBML magic up to its first
 * Cluster. Covers the codecs whose configuration cannot be inferred (H.264,
 * HEVC, AAC) because it copies the CodecPrivate rather than guessing it.
 */
async function headerFrom(referenceFile, opts) {
    var o = opts || {};
    var limit = o.limit || (8 * 1048576);
    var buf = await readAt(referenceFile, 0, limit);
    if (!(buf[0] === 0x1a && buf[1] === 0x45 && buf[2] === 0xdf && buf[3] === 0xa3)) return null;
    for (var i = 0; i + 16 <= buf.length; i++) {
        if (buf[i] !== CLUSTER_ID[0]) continue;
        if (clusterHere(buf, i)) return buf.subarray(0, i);
    }
    return null;
}

/**
 * Rebuild. `opts.reference` takes the donor lane; otherwise the codecs are
 * sniffed. Returns the candidate and what it believes — never a verdict on
 * whether it is right: the caller decodes it to find that out.
 */
async function rebuild(file, opts) {
    var o = opts || {};
    var at = o.at;
    if (at === undefined || at === null) {
        var found = await findClusters(file, o);
        if (!found) return null;
        at = found.at;
    }

    var tail = file.slice(at, file.size);

    if (o.reference) {
        var head = await headerFrom(o.reference);
        if (head) {
            return { blob: new Blob([head, tail], { type: o.mime || 'video/webm' }),
                     lane: 'reference', at: at, headerBytes: head.length, tracks: null };
        }
    }

    var info = await examine(file, { at: at });
    var named = info.tracks.filter(function (t) { return !!t.codec; });
    if (!named.length) return null;
    var durationMs = o.durationMs;
    if (durationMs === undefined) {
        try { durationMs = await tailDurationMs(file, at, o.timestampScale || 1000000); }
        catch (_) { durationMs = 0; }
    }
    var docType = o.docType ||
        (named.every(function (t) { return /^(V_VP8|V_VP9|V_AV1|A_OPUS)$/.test(t.codec); })
            ? 'webm' : 'matroska');
    var header = buildHeader({
        docType: docType, tracks: info.tracks,
        timestampScale: o.timestampScale || 1000000,
        durationMs: durationMs || 0,
    });
    return {
        blob: new Blob([header, tail], { type: docType === 'webm' ? 'video/webm' : 'video/x-matroska' }),
        lane: 'sniff', at: at, headerBytes: header.length,
        tracks: info.tracks, docType: docType, clusters: info.clusters,
        durationMs: durationMs || 0,
    };
}

/* sniffVideo/sniffAudio are exported for tests/node/matroska-surgeon.test.mjs.
 * They are the two functions every defect in this module has lived in, and the
 * only way to hold them against REAL encoder bytes without an ffmpeg round-trip
 * is to call them directly — driving them through examine() would mean building
 * the fixture with this module's own writer, i.e. using the generator as its own
 * oracle. */
export { CLUSTER_ID, findClusters, countClusters, examine, rebuild, headerFrom,
         buildHeader, clusterHere, clusterSpan, tailDurationMs, sniffVideo, sniffAudio };

/* The blade's inline JS reaches this by name, the way it reaches MediaLocate. */
if (typeof window !== 'undefined') {
  window.MatroskaSurgeon = {
    CLUSTER_ID: CLUSTER_ID, findClusters: findClusters, countClusters: countClusters,
    examine: examine, rebuild: rebuild, headerFrom: headerFrom, buildHeader: buildHeader,
    clusterHere: clusterHere, clusterSpan: clusterSpan,
    tailDurationMs: tailDurationMs, version: '1.0.0',
  };
}
